/**
 * Competition Detail Page
 * View competition details, leaderboard, and log progress
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Trophy, ArrowLeft, Users, Target, Calendar, Clock,
  TrendingUp, Award, Crown, Medal, Star, Plus, Activity,
  BarChart3, Edit, Trash2, CheckCircle, XCircle, History,
  Send, AlertCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Status badge configurations
const STATUS_CONFIG = {
  DRAFT: { label: 'Draft', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  ACTIVE: { label: 'Active', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  COMPLETED: { label: 'Completed', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  CANCELLED: { label: 'Cancelled', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' }
};

// Category configurations
const CATEGORY_CONFIG = {
  SALES: { label: 'Sales', className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  OPS: { label: 'Operations', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  PRODUCTION: { label: 'Production', className: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' },
  CREWS: { label: 'Crews', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  MARKETING: { label: 'Marketing', className: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400' },
  LEADERSHIP: { label: 'Leadership', className: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
  GENERAL: { label: 'General', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' }
};

// Metric type formatters
const formatMetricValue = (value, metricType) => {
  switch (metricType) {
    case 'CURRENCY':
      return `$${value?.toLocaleString() || 0}`;
    case 'PERCENT':
      return `${value || 0}%`;
    case 'HOURS':
      return `${value || 0}h`;
    case 'POINTS':
      return `${value || 0} pts`;
    default:
      return value?.toLocaleString() || 0;
  }
};

// Scoring rule labels
const SCORING_LABELS = {
  HIGHEST_WINS: 'Highest Wins',
  FIRST_TO_TARGET: 'First to Target',
  CLOSEST_TO_TARGET: 'Closest to Target',
  TEAM_SUM: 'Team Sum',
  TEAM_AVERAGE: 'Team Average'
};

// Leaderboard Entry Component
const LeaderboardRow = ({ entry, metricType, targetValue, isCurrentUser }) => {
  const getRankBadge = (rank) => {
    if (rank === 1) return <Crown className="h-5 w-5 text-amber-500" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-slate-400" />;
    if (rank === 3) return <Medal className="h-5 w-5 text-amber-700" />;
    return <span className="w-5 h-5 flex items-center justify-center text-sm font-medium">{rank}</span>;
  };

  return (
    <div 
      className={`flex items-center gap-4 p-3 rounded-lg transition-colors ${
        isCurrentUser ? 'bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800' : 'hover:bg-accent/50'
      }`}
    >
      <div className="w-8 flex justify-center">
        {getRankBadge(entry.rank)}
      </div>
      <Avatar className="h-10 w-10">
        <AvatarFallback>{entry.entity_name?.charAt(0) || '?'}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{entry.entity_name}</p>
        <p className="text-xs text-muted-foreground">
          {entry.entries_count} entries
          {entry.last_entry_at && ` • Last: ${new Date(entry.last_entry_at).toLocaleDateString()}`}
        </p>
      </div>
      <div className="text-right">
        <p className="font-semibold text-lg">
          {formatMetricValue(entry.score, metricType)}
        </p>
        <Progress 
          value={entry.progress_percent} 
          className="w-24 h-2"
        />
      </div>
    </div>
  );
};

// Activity Log Entry Component  
const ActivityEntry = ({ entry }) => {
  const getActionIcon = (action) => {
    switch (action) {
      case 'entry_added': return <Plus className="h-4 w-4 text-green-500" />;
      case 'entry_corrected': return <Edit className="h-4 w-4 text-amber-500" />;
      case 'participant_joined': return <Users className="h-4 w-4 text-blue-500" />;
      case 'competition_completed': return <CheckCircle className="h-4 w-4 text-purple-500" />;
      case 'competition_activated': return <Activity className="h-4 w-4 text-green-500" />;
      default: return <Activity className="h-4 w-4 text-slate-500" />;
    }
  };

  const getActionLabel = (action) => {
    switch (action) {
      case 'entry_added': return 'logged progress';
      case 'entry_corrected': return 'corrected an entry';
      case 'participant_joined': return 'joined the competition';
      case 'participant_declined': return 'declined invitation';
      case 'competition_completed': return 'Competition completed';
      case 'competition_activated': return 'activated the competition';
      case 'competition_created': return 'created the competition';
      default: return action.replace(/_/g, ' ');
    }
  };

  return (
    <div className="flex items-start gap-3 py-2">
      <div className="mt-1">{getActionIcon(entry.action)}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm">
          <span className="font-medium">{entry.actor_name}</span>
          {' '}{getActionLabel(entry.action)}
          {entry.details?.value && (
            <span className="text-muted-foreground">
              {' '}(+{entry.details.value})
            </span>
          )}
        </p>
        <p className="text-xs text-muted-foreground">
          {new Date(entry.created_at).toLocaleString()}
        </p>
      </div>
    </div>
  );
};

// Main Component
export const CompetitionDetail = () => {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [competition, setCompetition] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [entries, setEntries] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Entry form state
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [entryValue, setEntryValue] = useState('');
  const [entryNote, setEntryNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const isLeadership = ['super_admin', 'admin', 'leadership', 'general_manager', 'ceo', 'president', 'director_of_sales'].includes(user?.role);
  const isOwner = competition?.created_by === user?.id;
  const isParticipant = competition?.my_status === 'ACCEPTED';
  const isActive = competition?.status === 'ACTIVE';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [compResponse, leaderboardResponse, participantsResponse, entriesResponse, activityResponse] = await Promise.all([
        axios.get(`${API}/competitions/${competitionId}?user_id=${user?.id}`),
        axios.get(`${API}/competitions/${competitionId}/leaderboard`),
        axios.get(`${API}/competitions/${competitionId}/participants`),
        axios.get(`${API}/competitions/${competitionId}/entries?limit=50`),
        axios.get(`${API}/competitions/${competitionId}/activity?limit=30`)
      ]);
      
      setCompetition(compResponse.data);
      setLeaderboard(leaderboardResponse.data?.entries || []);
      setParticipants(participantsResponse.data || []);
      setEntries(entriesResponse.data || []);
      setActivityLog(activityResponse.data || []);
      
    } catch (error) {
      console.error('Error fetching competition:', error);
      toast.error('Failed to load competition');
    } finally {
      setLoading(false);
    }
  }, [competitionId, user?.id]);

  useEffect(() => {
    if (user?.id) {
      fetchData();
    }
  }, [fetchData, user?.id]);

  const handleLogProgress = async () => {
    if (!entryValue || isNaN(parseFloat(entryValue))) {
      toast.error('Please enter a valid number');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/competitions/${competitionId}/entries?user_id=${user?.id}`, {
        value: parseFloat(entryValue),
        note: entryNote || null
      });
      
      toast.success('Progress logged!');
      setShowEntryModal(false);
      setEntryValue('');
      setEntryNote('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log progress');
    } finally {
      setSubmitting(false);
    }
  };

  const handleActivate = async () => {
    try {
      await axios.post(`${API}/competitions/${competitionId}/activate?user_id=${user?.id}`);
      toast.success('Competition activated!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate');
    }
  };

  const handleComplete = async () => {
    if (!window.confirm('Are you sure you want to complete this competition? This will determine the winner.')) {
      return;
    }

    try {
      await axios.post(`${API}/competitions/${competitionId}/complete?user_id=${user?.id}`);
      toast.success('Competition completed!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete');
    }
  };

  const handleJoin = async () => {
    try {
      await axios.post(`${API}/competitions/${competitionId}/join?user_id=${user?.id}`);
      toast.success('Joined competition!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 bg-slate-200 dark:bg-slate-800 rounded" />
            <div className="h-64 bg-slate-200 dark:bg-slate-800 rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!competition) {
    return (
      <Layout>
        <div className="p-6 max-w-6xl mx-auto text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Competition not found</h2>
          <Button onClick={() => navigate('/competition')}>Back to Overview</Button>
        </div>
      </Layout>
    );
  }

  const statusConfig = STATUS_CONFIG[competition.status] || STATUS_CONFIG.DRAFT;
  const categoryConfig = CATEGORY_CONFIG[competition.category] || CATEGORY_CONFIG.GENERAL;
  
  const timeRemaining = competition.end_at 
    ? Math.max(0, Math.ceil((new Date(competition.end_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="competition-detail">
        {/* Header */}
        <div className="flex items-start gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/competition')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge className={categoryConfig.className}>{categoryConfig.label}</Badge>
              <Badge className={statusConfig.className}>{statusConfig.label}</Badge>
              {competition.is_challenge && (
                <Badge variant="outline">Challenge</Badge>
              )}
            </div>
            <h1 className="text-2xl font-bold">{competition.title}</h1>
            {competition.description && (
              <p className="text-muted-foreground mt-1">{competition.description}</p>
            )}
          </div>
          <div className="flex gap-2">
            {isActive && isParticipant && (
              <Button onClick={() => setShowEntryModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Log Progress
              </Button>
            )}
            {isActive && !isParticipant && competition.allow_open_join && (
              <Button onClick={handleJoin}>Join Competition</Button>
            )}
            {(isLeadership || isOwner) && competition.status === 'DRAFT' && (
              <Button onClick={handleActivate}>Activate</Button>
            )}
            {(isLeadership || isOwner) && isActive && (
              <Button variant="outline" onClick={handleComplete}>Complete</Button>
            )}
          </div>
        </div>

        {/* Winner Announcement (if completed) */}
        {competition.status === 'COMPLETED' && competition.winner_name && (
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border-amber-300 dark:border-amber-700">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Avatar className="h-16 w-16 border-4 border-amber-400">
                    <AvatarFallback className="text-xl bg-amber-100 text-amber-700">
                      {competition.winner_name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <Crown className="absolute -top-2 -right-2 h-8 w-8 text-amber-500" />
                </div>
                <div>
                  <p className="text-sm text-amber-700 dark:text-amber-400 font-medium">🎉 Winner</p>
                  <h2 className="text-2xl font-bold">{competition.winner_name}</h2>
                  {competition.props_message && (
                    <p className="text-muted-foreground italic mt-1">"{competition.props_message}"</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Target className="h-4 w-4" />
                <span className="text-sm">Goal</span>
              </div>
              <p className="text-2xl font-bold">
                {formatMetricValue(competition.target_value, competition.metric_type)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Users className="h-4 w-4" />
                <span className="text-sm">Participants</span>
              </div>
              <p className="text-2xl font-bold">{competition.participant_count}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <BarChart3 className="h-4 w-4" />
                <span className="text-sm">Scoring</span>
              </div>
              <p className="text-lg font-medium">{SCORING_LABELS[competition.scoring_rule]}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Clock className="h-4 w-4" />
                <span className="text-sm">
                  {competition.status === 'COMPLETED' ? 'Duration' : 'Time Left'}
                </span>
              </div>
              <p className="text-2xl font-bold">
                {competition.status === 'COMPLETED' 
                  ? 'Ended'
                  : timeRemaining !== null 
                    ? `${timeRemaining}d`
                    : 'Ongoing'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* My Progress (if participant) */}
        {isParticipant && competition.my_score !== undefined && (
          <Card className="border-blue-200 dark:border-blue-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Medal className="h-5 w-5 text-blue-500" />
                Your Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-2">
                <span className="text-3xl font-bold">
                  {formatMetricValue(competition.my_score, competition.metric_type)}
                </span>
                <Badge className="text-lg px-3 py-1">
                  Rank #{competition.my_rank || '-'}
                </Badge>
              </div>
              <Progress 
                value={Math.min((competition.my_score / competition.target_value) * 100, 100)} 
                className="h-3"
              />
              <p className="text-sm text-muted-foreground mt-2">
                {Math.round((competition.my_score / competition.target_value) * 100)}% of goal
              </p>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
            <TabsTrigger value="participants">Participants ({participants.length})</TabsTrigger>
            <TabsTrigger value="activity">Activity Log</TabsTrigger>
            {(isLeadership || isOwner) && (
              <TabsTrigger value="settings">Settings</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="overview" className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Top 5 Leaderboard */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-amber-500" />
                    Top Performers
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {leaderboard.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No entries yet. Be the first to log progress!
                    </p>
                  ) : (
                    leaderboard.slice(0, 5).map(entry => (
                      <LeaderboardRow
                        key={entry.entity_id}
                        entry={entry}
                        metricType={competition.metric_type}
                        targetValue={competition.target_value}
                        isCurrentUser={entry.entity_id === user?.id}
                      />
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Recent Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {activityLog.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No activity yet.
                    </p>
                  ) : (
                    <div className="space-y-1 divide-y">
                      {activityLog.slice(0, 8).map(entry => (
                        <ActivityEntry key={entry.id} entry={entry} />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Competition Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Competition Details</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm text-muted-foreground">Type</dt>
                    <dd className="font-medium">{competition.competition_type}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Metric</dt>
                    <dd className="font-medium">{competition.metric_type}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Start Date</dt>
                    <dd className="font-medium">
                      {competition.start_at 
                        ? new Date(competition.start_at).toLocaleDateString()
                        : 'Not set'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">End Date</dt>
                    <dd className="font-medium">
                      {competition.end_at 
                        ? new Date(competition.end_at).toLocaleDateString()
                        : 'Not set'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Created By</dt>
                    <dd className="font-medium">{competition.created_by_name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Visibility</dt>
                    <dd className="font-medium">{competition.visibility}</dd>
                  </div>
                  {competition.reward_description && (
                    <div className="col-span-2">
                      <dt className="text-sm text-muted-foreground">Reward</dt>
                      <dd className="font-medium">{competition.reward_description}</dd>
                    </div>
                  )}
                </dl>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="leaderboard" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Full Leaderboard</CardTitle>
                <CardDescription>
                  Ranked by {SCORING_LABELS[competition.scoring_rule]?.toLowerCase()}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {leaderboard.length === 0 ? (
                  <div className="text-center py-8">
                    <Trophy className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">No entries yet</p>
                  </div>
                ) : (
                  leaderboard.map(entry => (
                    <LeaderboardRow
                      key={entry.entity_id}
                      entry={entry}
                      metricType={competition.metric_type}
                      targetValue={competition.target_value}
                      isCurrentUser={entry.entity_id === user?.id}
                    />
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="participants" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Participants</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {participants.map(p => (
                    <div key={p.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent/50">
                      <Avatar>
                        <AvatarFallback>{p.entity_name?.charAt(0) || '?'}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-medium">{p.entity_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {p.entity_type === 'TEAM' ? 'Team' : 'Individual'}
                        </p>
                      </div>
                      <Badge variant={p.status === 'ACCEPTED' ? 'default' : 'outline'}>
                        {p.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="activity" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Activity Log</CardTitle>
                <CardDescription>All competition activity</CardDescription>
              </CardHeader>
              <CardContent>
                {activityLog.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No activity yet</p>
                ) : (
                  <div className="space-y-1 divide-y">
                    {activityLog.map(entry => (
                      <ActivityEntry key={entry.id} entry={entry} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {(isLeadership || isOwner) && (
            <TabsContent value="settings" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>Competition Settings</CardTitle>
                  <CardDescription>Manage competition configuration</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      onClick={() => navigate(`/competition/competitions/${competitionId}/edit`)}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Competition
                    </Button>
                    {competition.status === 'DRAFT' && (
                      <Button onClick={handleActivate}>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Activate Now
                      </Button>
                    )}
                    {competition.status === 'ACTIVE' && (
                      <Button variant="outline" onClick={handleComplete}>
                        Complete Competition
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Log Progress Modal */}
        <Dialog open={showEntryModal} onOpenChange={setShowEntryModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Log Progress</DialogTitle>
              <DialogDescription>
                Add your progress for this competition
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Value ({competition.metric_type})</Label>
                <Input
                  type="number"
                  placeholder={`Enter ${competition.metric_type.toLowerCase()} value`}
                  value={entryValue}
                  onChange={(e) => setEntryValue(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Current: {formatMetricValue(competition.my_score || 0, competition.metric_type)} → 
                  {' '}New Total: {formatMetricValue((competition.my_score || 0) + (parseFloat(entryValue) || 0), competition.metric_type)}
                </p>
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Textarea
                  placeholder="Add a note about this progress..."
                  value={entryNote}
                  onChange={(e) => setEntryNote(e.target.value)}
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEntryModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleLogProgress} disabled={submitting}>
                {submitting ? 'Saving...' : 'Log Progress'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default CompetitionDetail;
